import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  model,
  output,
  signal,
} from '@angular/core';
import { ControlValueAccessor, FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { translationSchema } from '@well/translation';
import { TranslateModule } from '@ngx-translate/core';
import { ColumnFilterComponentBase } from '@irian-ro/table-column-filter';
import { FilterMatchMode, FilterMetadata } from 'primeng/api';
import { Popover } from 'primeng/popover';
import { DatePicker } from 'primeng/datepicker';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';

export type DateTimeRangeValue = {
  start: Date | null;
  end: Date | null;
};

@Component({
  selector: 'lib-date-time-range-context-menu-filter',
  standalone: true,
  imports: [
    DatePicker,
    FormsModule,
    InputTextModule,
    TranslateModule,
    Popover,
    Popover,
    DatePicker,
    IconField,
    InputIcon,
  ],
  templateUrl: './date-time-range-context-menu-filter.component.html',
  styleUrl: './date-time-range-context-menu-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateTimeRangeContextMenuFilterComponent implements ControlValueAccessor, ColumnFilterComponentBase {
  private hostEl = inject(ElementRef<HTMLElement>);

  placeholder = model<string>('');

  readonly open = signal(false);
  readonly disabled = signal(false);
  readonly touched = signal(false);

  draftStart = signal<Date | null>(null);
  draftEnd = signal<Date | null>(null);

  value = model<DateTimeRangeValue>({ start: null, end: null });

  snapshot: DateTimeRangeValue = { start: null, end: null };

  draftStartTime = signal<string>('00:00');
  draftEndTime = signal<string>('00:00');

  displayText = computed(() => {
    const { start, end } = this.value();
    if (!start && !end) return this.placeholder();

    const fmt = (date: Date) => {
      if (!date) {
        return '';
      }
      const d = new Date(date);

      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${y}-${m}-${day} ${hh}:${mm}`;
    };

    if (start && end) return `${fmt(start)} → ${fmt(end)}`;
    return start ? fmt(start) : end ? fmt(end) : this.placeholder();
  });

  filtersChange = output<FilterMetadata>();

  private onChange: (val: DateTimeRangeValue) => void = () => {
    return;
  };
  private onTouched: () => void = () => {
    return;
  };

  writeValue(val: DateTimeRangeValue | null): void {
    const next: DateTimeRangeValue = val ?? { start: null, end: null };
    this.value.set({
      start: next.start ? new Date(next.start) : null,
      end: next.end ? new Date(next.end) : null,
    });

    this.snapshot = this.cloneRange(this.value());
  }

  registerOnChange(fn: (val: DateTimeRangeValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    if (isDisabled) this.open.set(false);
  }

  toggleOpen(): void {
    if (this.disabled()) return;

    const nowOpen = !this.open();
    this.open.set(nowOpen);

    if (nowOpen) {
      const committed = this.value();
      this.snapshot = this.cloneRange(committed);

      this.draftStart.set(committed.start ? new Date(committed.start) : null);
      this.draftEnd.set(committed.end ? new Date(committed.end) : null);

      this.draftStartTime.set(this.getHHmm(committed.start) ?? '00:00');
      this.draftEndTime.set(this.getHHmm(committed.end) ?? '00:00');
    } else {
      this.setDateTimeRange();
      this.markTouched();
    }
  }

  setDateTimeRange(): void {
    const merged = this.mergeDraftsToRange();
    this.value.set(merged);

    this.onChange(this.cloneRange(merged));
    this.markTouched();
    this.filtersChange.emit({
      value: this.value(),
      matchMode: FilterMatchMode.BETWEEN,
    });
  }

  setDraftStartDate(d: Date | null): void {
    this.draftStart.set(d ? new Date(d) : null);

    if (this.compareDraftDates() > 0) {
      this.draftEnd.set(null);
    }

    this.setDateTimeRange();
  }
  setDraftEndDate(d: Date | null): void {
    this.draftEnd.set(d ? new Date(d) : null);

    const startTime = this.draftStartTime();
    const endTime = this.draftEndTime();
    if (this.compareDraftDates() === 0 && startTime && endTime && endTime.localeCompare(startTime) < 0) {
      this.draftEndTime.set(startTime);
    }

    this.setDateTimeRange();
  }

  setDraftStartTime(t: string): void {
    this.draftStartTime.set(this.normalizeTime(t));
    const startTime = this.draftStartTime();
    const endTime = this.draftEndTime();

    if (this.compareDraftDates() === 0 && startTime && endTime && startTime.localeCompare(endTime) > 0) {
      this.draftEndTime.set(startTime);
    }

    this.setDateTimeRange();
  }
  setDraftEndTime(t: string): void {
    const startTime = this.draftStartTime();
    const endTime = this.normalizeTime(t);

    if (this.compareDraftDates() === 0 && startTime && endTime && endTime.localeCompare(startTime) < 0) {
      this.draftEndTime.set(startTime);
    } else {
      this.draftEndTime.set(endTime);
    }

    this.setDateTimeRange();
  }

  @HostListener('document:mousedown', ['$event'])
  onDocMouseDown(e: MouseEvent): void {
    if (!this.open()) return;
    const target = e.target as Node | null;
    if (!target) return;

    // If click outside the component, close
    if (!this.hostEl.nativeElement.contains(target)) {
      this.open.set(false);
      this.markTouched();
    }
  }

  private markTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
      this.onTouched();
    }
  }

  private mergeDraftsToRange(): DateTimeRangeValue {
    const s = this.applyTime(this.draftStart(), this.draftStartTime());
    const e = this.applyTime(this.draftEnd(), this.draftEndTime());
    return { start: s, end: e };
  }

  private applyTime(date: Date | null, hhmm: string): Date | null {
    if (!date) return null;
    const d = new Date(date);
    const [hh, mm] = hhmm.split(':').map((n) => Number(n));
    d.setHours(Number.isFinite(hh) ? hh : 0, Number.isFinite(mm) ? mm : 0, 0, 0);
    return d;
  }

  private getHHmm(date: Date | null): string | null {
    if (!date) {
      return null;
    }

    const d = new Date(date);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  private normalizeTime(raw: string): string {
    const digits = raw.replace(/\D/g, '').slice(0, 4);

    const hh = digits.slice(0, 2);
    const mm = digits.slice(2, 4);

    let h = hh ? Number(hh) : 0;
    let m = mm ? Number(mm) : 0;

    if (!Number.isFinite(h)) h = 0;
    if (!Number.isFinite(m)) m = 0;

    h = Math.max(0, Math.min(23, h));
    m = Math.max(0, Math.min(59, m));

    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  private cloneRange(r: DateTimeRangeValue): DateTimeRangeValue {
    return {
      start: r.start ? new Date(r.start) : null,
      end: r.end ? new Date(r.end) : null,
    };
  }

  private compareDraftDates() {
    const startDate = this.draftStart();
    const endDate = this.draftEnd();

    if (!startDate) return 1;
    if (!endDate) return -1;

    return startDate.toISOString().localeCompare(endDate.toISOString());
  }

  protected readonly translationSchema = translationSchema;
}
